import { useContext, useEffect, useMemo, useState } from 'react';
import {
    Chart,
    Series,
    CommonSeriesSettings,
    Legend,
    ValueAxis,
    Title,
    Export,
    Tooltip,
    ArgumentAxis,
} from 'devextreme-react/chart';
import {
    DataGrid,
    Column,
    FilterRow,
    HeaderFilter,
    LoadPanel,
    Scrolling,
    Paging,
} from "devextreme-react/data-grid";
import { Close as CloseIcon } from "@mui/icons-material";
import { ChartFilterTagBox as TagBox } from './ChartFilterTagBox';
import { Popover } from 'devextreme-react/popover';
import { convertUTCTimeToLocal, handleK1PointClick, STATUS_COLOR_MAP } from '../FSServices';
import { createSitesClientApi } from '../../../api-client-nswag/taxportal-client-runtime';
import { Box, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import DatasetLinkedIcon from '@mui/icons-material/DatasetLinked';
import { OidcIdentityContext } from '@rsmus/react-auth';

// ─── Formatters ──────────────────────────────────────────────────────────────

function customizeTooltip(arg: { seriesName: string; valueText: string; argument: string }) {
    return {
        text: `${arg.argument}\n${arg.seriesName}: ${arg.valueText}`,
    };
}

function getLegendTargetKey(event: any) {
    return event?.target?.name || null;
}

function getK1FilterData(
    fundData: any[],
    fundFilter: string[],
    statusFilter: string[],
    entityFilter: string[],
    receivedField: string,
    pendingField: string,
    legendFilter: string[],
) {
    const matchesFund = (row: any) => fundFilter.length === 0 || fundFilter.includes(row.fund);
    const matchesEntity = (row: any) => entityFilter.length === 0 || entityFilter.includes(row.entity || row.entityName);
    const matchesStatus = (row: any) => [statusFilter, legendFilter].every((statuses) => statuses.length === 0
        || (statuses.includes('Received') && Number(row[receivedField]) > 0)
        || (statuses.includes('Pending') && Number(row[pendingField]) > 0));
    const fundOptions = Array.from(new Set(fundData
        .filter((row) => matchesEntity(row) && matchesStatus(row))
        .map((row) => row.fund).filter(Boolean))).sort() as string[];
    const entityOptions = Array.from(new Set(fundData
        .filter((row) => matchesFund(row) && matchesStatus(row))
        .map((row) => row.entity || row.entityName).filter(Boolean))).sort() as string[];
    const statusRows = fundData.filter((row) => matchesFund(row) && matchesEntity(row));
    const statusOptions = ['Received', 'Pending'].filter((status) => statusRows.some((row) =>
        Number(row[status === 'Received' ? receivedField : pendingField]) > 0
    ));
    const filteredFundData = fundData.filter((row) => matchesFund(row) && matchesEntity(row) && matchesStatus(row));

    return { fundOptions, entityOptions, statusOptions, filteredFundData };
}


// ─── Data Hook ───────────────────────────────────────────────────────────────


// ─── Sub Charts — receive onRefresh as prop ───────────────────────────────────

interface ChartProps {
    fundData: any[];
    onRefresh: () => void;
    siteId: number;
    lastRefreshTime: string;
}

interface K1ChartHeaderProps {
    title: string;
    filterTriggerId: string;
    isFilterOpen: boolean;
    activeFilterCount: number;
    onToggleFilter: () => void;
    onRefresh: () => void;
    lastRefreshedTime: string;
    onDataPointsClick?: () => void;
}

interface K1FilterPopoverProps {
    targetId: string;
    visible: boolean;
    onHiding: () => void;
    fundOptions: string[];
    fundFilter: string[];
    onFundFilterChange: (value: string[]) => void;
    statusLabel: string;
    statusOptions: string[];
    statusFilter: string[];
    onStatusFilterChange: (value: string[]) => void;
    entityOptions: string[];
    entityFilter: string[];
    onEntityFilterChange: (value: string[]) => void;
    activeFilterCount: number;
    onClearAllFilters: () => void;
}

function useK1LastRefreshedTime(siteId: number, fundData: any[]) {
    const { user } = useContext(OidcIdentityContext);
    const [lastRefreshedTime, setLastRefreshedTime] = useState('');

    useEffect(() => {
        const fetchItemTrack = async () => {
            const itemTrack = await createSitesClientApi.taxportalDbData_GetItemSyncTrackByType(siteId, 'SYNC_K1_TRACKER', user?.profile.preferred_username as string);
            const lastModified = (itemTrack as any)?.data?.lastModified ?? (itemTrack as any)?.lastModified
            const localTime = lastModified?.trim() ? convertUTCTimeToLocal(lastModified) : '';
            setLastRefreshedTime(localTime);
        };

        fetchItemTrack();
    }, [siteId, fundData, user?.profile.preferred_username]);

    return lastRefreshedTime;
}

function K1ChartHeader(props: K1ChartHeaderProps) {
    return (
        <div className="chart-header">
            <div className="chart-title-section1">
                <div className="chart-title-row1">
                    <h3 className="chart-title1">{props.title}</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div
                            onClick={props.onDataPointsClick}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: 4,
                                border: '1px solid #d0d0d0',
                                cursor: 'pointer',
                                backgroundColor: '#fff',
                            }}
                            title="Data Points"
                        >
                            <DatasetLinkedIcon sx={{ fontSize: 16, color: '#555' }} />
                        </div>
                        <div
                            id={props.filterTriggerId}
                            onClick={props.onToggleFilter}
                            className={`fs-chart-filter-trigger${props.activeFilterCount > 0 ? ' fs-chart-filter-trigger--active' : ''}`}
                            title="Filters"
                        >
                            <i className="dx-icon-filter" style={{ fontSize: 16, color: '#555' }} aria-hidden="true" />
                            {props.activeFilterCount > 0 && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: -4,
                                        right: -4,
                                        minWidth: 16,
                                        height: 16,
                                        borderRadius: 8,
                                        backgroundColor: '#1976d2',
                                        color: '#fff',
                                        fontSize: 10,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0 3px',
                                    }}
                                >
                                    {props.activeFilterCount}
                                </span>
                            )}
                        </div>
                        <div
                            onClick={props.onRefresh}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: 4,
                                border: '1px solid #d0d0d0',
                                cursor: 'pointer',
                                backgroundColor: '#fff',
                            }}
                            title="Refresh"
                        >
                            <i className="dx-icon-refresh" style={{ fontSize: 16, color: '#555' }} aria-hidden="true" />
                        </div>
                    </div>
                </div>
                <span className="chart-refresh-time1">Last Refreshed: {props.lastRefreshedTime}</span>
            </div>
        </div>
    );
}

function K1FilterPopover(props: K1FilterPopoverProps) {
    return (
        <Popover
            target={`#${props.targetId}`}
            visible={props.visible}
            onHiding={props.onHiding}
            hideOnOutsideClick={true}
            position="bottom"
            width={280}
        >
            <div className="fs-chart-filter-content" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 4 }}>
                <div>
                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Fund</div>
                    <TagBox
                        dataSource={props.fundOptions}
                        value={props.fundFilter}
                        onValueChanged={(e) => props.onFundFilterChange(e.value ?? [])}
                        placeholder="All funds"
                        showSelectionControls={true}
                        applyValueMode="instantly"
                        searchEnabled={true}
                        width="100%"
                    />
                </div>

                <div>
                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>{props.statusLabel}</div>
                    <TagBox
                        dataSource={props.statusOptions}
                        value={props.statusFilter}
                        onValueChanged={(e) => props.onStatusFilterChange(e.value ?? [])}
                        placeholder="All"
                        showSelectionControls={true}
                        applyValueMode="instantly"
                        width="100%"
                    />
                </div>

                {(props.entityOptions.length > 0 || props.entityFilter.length > 0) && (
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Entity</div>
                        <TagBox
                            dataSource={props.entityOptions}
                            value={props.entityFilter}
                            onValueChanged={(e) => props.onEntityFilterChange(e.value ?? [])}
                            placeholder="All entities"
                            showSelectionControls={true}
                            applyValueMode="instantly"
                            searchEnabled={true}
                            width="100%"
                        />
                    </div>
                )}

                {props.activeFilterCount > 0 && (
                    <button
                        type="button"
                        onClick={props.onClearAllFilters}
                        style={{
                            fontSize: 12,
                            alignSelf: 'flex-start',
                            background: 'none',
                            border: 'none',
                            color: '#1976d2',
                            cursor: 'pointer',
                            padding: 0,
                        }}
                    >
                        Clear all filters
                    </button>
                )}
            </div>
        </Popover>
    );
}

function K1DataPointsDialog(props: { open: boolean; onClose: () => void; title: string; rows: any[] }) {

    const HAS_COUNT_DATA = Object.freeze([
        { value: 1, display: "Yes" },
        { value: 0, display: "No" },
    ]);

    const hasCountLookup = useMemo(
        () => ({
            dataSource: HAS_COUNT_DATA,
            valueExpr: "value",
            displayExpr: "display",
        }),
        []
    );
    return (
        <Dialog open={props.open} onClose={props.onClose} maxWidth="lg" fullWidth>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 1,
                    borderBottom: "1px solid #e0e0e0",
                }}
            >
                <Typography variant="h6">
                    {props.title}
                </Typography>

                <IconButton
                    aria-label="close"
                    onClick={props.onClose}
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent
                sx={{
                    p: 2,
                }}
            >
                <DataGrid
                    dataSource={props.rows}
                    height={450}
                    showBorders
                    rowAlternationEnabled
                    columnAutoWidth
                    wordWrapEnabled
                    hoverStateEnabled
                    noDataText="No data available"
                >
                    <LoadPanel enabled={false} />

                    <FilterRow visible />

                    <HeaderFilter visible />

                    <Paging
                        enabled
                        defaultPageSize={20}
                    />

                    <Scrolling mode="standard" />

                    <Column
                        dataField="fund"
                        caption="Fund"
                    />

                    <Column
                        caption="Entity"
                        calculateCellValue={(rowData: any) =>
                            rowData?.entityName || rowData?.entity || ""
                        }
                    />

                    <Column
                        dataField="estimatedYes"
                        caption="Estimated Received"
                        dataType="number" lookup={hasCountLookup}
                    />

                    <Column
                        dataField="estimatedNo"
                        caption="Estimated Pending"
                        dataType="number" lookup={hasCountLookup}
                    />

                    <Column
                        dataField="finalizedYes"
                        caption="Finalized Received"
                        dataType="number" lookup={hasCountLookup}
                    />

                    <Column
                        dataField="finalizedNo"
                        caption="Finalized Pending"
                        dataType="number" lookup={hasCountLookup}
                    />
                </DataGrid>
            </DialogContent>
        </Dialog>
    );
}


export function K1StatusByFundEstimatedChart(props: ChartProps) {
    const fundData = props.fundData;
    const [activeSeries, setActiveSeries] = useState<string[]>([]);
    const [fundFilter, setFundFilter] = useState<string[]>([]);
    const [estimatedFilter, setEstimatedFilter] = useState<string[]>([]);
    const [entityFilter, setEntityFilter] = useState<string[]>([]);
    const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
    const [isDataViewOpen, setIsDataViewOpen] = useState(false);
    const lastRefreshedTime = useK1LastRefreshedTime(props.siteId, fundData);

    const { fundOptions, entityOptions, statusOptions: estimatedOptions, filteredFundData } = getK1FilterData(
        fundData, fundFilter, estimatedFilter, entityFilter, 'estimatedYes', 'estimatedNo',
        activeSeries.map((name) => name === 'Estimated Received' ? 'Received' : 'Pending'),
    );

    const showReceived = estimatedFilter.length === 0 || estimatedFilter.includes('Received');
    const showPending = estimatedFilter.length === 0 || estimatedFilter.includes('Pending');

    const activeFilterCount =
        (fundFilter.length > 0 ? 1 : 0) +
        (estimatedFilter.length > 0 ? 1 : 0) +
        (entityFilter.length > 0 ? 1 : 0);

    const clearAllFilters = () => {
        setFundFilter([]);
        setEstimatedFilter([]);
        setEntityFilter([]);
        setActiveSeries([]);
    };

    const handleLegendClick = (event: any) => {
        event.cancel = true;
        const legendKey = getLegendTargetKey(event);
        if (!legendKey) return;
        setActiveSeries((curr) =>
            curr.includes(legendKey)
                ? curr.filter((name) => name !== legendKey)
                : [...curr, legendKey]
        );
    };

    const handlePointClick = (event: any) => handleK1PointClick(event, props.siteId, 'Federal and State K-1 Estimates Received');


    return (
        <div>
            <K1ChartHeader
                title="K1 - Status by Fund - Estimated"
                filterTriggerId="k1-estimated-filter-icon-trigger"
                isFilterOpen={isFilterPopoverOpen}
                activeFilterCount={activeFilterCount}
                onToggleFilter={() => setIsFilterPopoverOpen((curr) => !curr)}
                onRefresh={props.onRefresh}
                lastRefreshedTime={lastRefreshedTime}
                onDataPointsClick={() => setIsDataViewOpen(true)}
            />

            <K1FilterPopover
                targetId="k1-estimated-filter-icon-trigger"
                visible={isFilterPopoverOpen}
                onHiding={() => setIsFilterPopoverOpen(false)}
                fundOptions={fundOptions}
                fundFilter={fundFilter}
                onFundFilterChange={setFundFilter}
                statusLabel="Estimated"
                statusOptions={estimatedOptions}
                statusFilter={estimatedFilter}
                onStatusFilterChange={setEstimatedFilter}
                entityOptions={entityOptions}
                entityFilter={entityFilter}
                onEntityFilterChange={setEntityFilter}
                activeFilterCount={activeFilterCount}
                onClearAllFilters={clearAllFilters}
            />

            {
                filteredFundData?.length > 0 ? (
                    <Chart id="fund-chart" dataSource={filteredFundData} height={Math.max(400, new Set(filteredFundData.map((row: any) => row.fund)).size * 32 + 120)} rotated={true} onLegendClick={handleLegendClick} onPointClick={handlePointClick}>
                        <CommonSeriesSettings argumentField="fund" type="stackedbar" />
                        <Series valueField="estimatedYes" name="Estimated Received" color={STATUS_COLOR_MAP['Estimated Received'] || '#888b8d'} visible={(activeSeries.length === 0 || activeSeries.includes('Estimated Received')) && showReceived} />
                        <Series valueField="estimatedNo" name="Estimated Pending" color={STATUS_COLOR_MAP['Estimated Pending'] || '#888b8d'} visible={(activeSeries.length === 0 || activeSeries.includes('Estimated Pending')) && showPending} />
                        {/* <Series valueField="finalizedYes" name="Finalized Received" color="#009cde" visible={activeSeries.length === 0 || activeSeries.includes('Finalized Received')} />
                        <Series valueField="finalizedNo" name="Finalized Pending" color="#ccebf8" visible={activeSeries.length === 0 || activeSeries.includes('Finalized Pending')} />
                         */}
                        <ValueAxis tickInterval={1} allowDecimals={false}><Title text="Count" /></ValueAxis>
                        <ArgumentAxis type="discrete" axisDivisionFactor={1} label={{ overlappingBehavior: 'none' }}><Title text="Fund" /></ArgumentAxis>
                        <Legend verticalAlignment="bottom" horizontalAlignment="center" itemTextPosition="top" />
                        <Export enabled={false} />
                        <Tooltip enabled={true} customizeTooltip={customizeTooltip} />
                    </Chart>
                ) : (
                    <div className="no-data-message">
                        No data available
                    </div>
                )
            }

            <K1DataPointsDialog
                open={isDataViewOpen}
                onClose={() => setIsDataViewOpen(false)}
                title="K1 - Status by Fund - Estimated"
                rows={filteredFundData}
            />
        </div>
    );
}

export function K1StatusByFundFinalizedChart(props: ChartProps) {
    const fundData = props.fundData;
    const [activeSeries, setActiveSeries] = useState<string[]>([]);
    const [fundFilter, setFundFilter] = useState<string[]>([]);
    const [finalizedFilter, setFinalizedFilter] = useState<string[]>([]);
    const [entityFilter, setEntityFilter] = useState<string[]>([]);
    const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
    const [isDataViewOpen, setIsDataViewOpen] = useState(false);
    const lastRefreshedTime = useK1LastRefreshedTime(props.siteId, fundData);


    const { fundOptions, entityOptions, statusOptions: finalizedOptions, filteredFundData } = getK1FilterData(
        fundData, fundFilter, finalizedFilter, entityFilter, 'finalizedYes', 'finalizedNo',
        activeSeries,
    );

    const showReceived = finalizedFilter.length === 0 || finalizedFilter.includes('Received');
    const showPending = finalizedFilter.length === 0 || finalizedFilter.includes('Pending');

    const activeFilterCount =
        (fundFilter.length > 0 ? 1 : 0) +
        (finalizedFilter.length > 0 ? 1 : 0) +
        (entityFilter.length > 0 ? 1 : 0);

    const clearAllFilters = () => {
        setFundFilter([]);
        setFinalizedFilter([]);
        setEntityFilter([]);
        setActiveSeries([]);
    };

    const handleLegendClick = (event: any) => {
        event.cancel = true;
        const legendKey = getLegendTargetKey(event);
        if (!legendKey) return;
        setActiveSeries((curr) =>
            curr.includes(legendKey)
                ? curr.filter((name) => name !== legendKey)
                : [...curr, legendKey]
        );
    };

    return (
        <div>
            <K1ChartHeader
                title="K1 - Status by Fund - Finalized"
                filterTriggerId="k1-finalized-filter-icon-trigger"
                isFilterOpen={isFilterPopoverOpen}
                activeFilterCount={activeFilterCount}
                onToggleFilter={() => setIsFilterPopoverOpen((curr) => !curr)}
                onRefresh={props.onRefresh}
                lastRefreshedTime={lastRefreshedTime}
                onDataPointsClick={() => setIsDataViewOpen(true)}
            />

            <K1FilterPopover
                targetId="k1-finalized-filter-icon-trigger"
                visible={isFilterPopoverOpen}
                onHiding={() => setIsFilterPopoverOpen(false)}
                fundOptions={fundOptions}
                fundFilter={fundFilter}
                onFundFilterChange={setFundFilter}
                statusLabel="Finalized"
                statusOptions={finalizedOptions}
                statusFilter={finalizedFilter}
                onStatusFilterChange={setFinalizedFilter}
                entityOptions={entityOptions}
                entityFilter={entityFilter}
                onEntityFilterChange={setEntityFilter}
                activeFilterCount={activeFilterCount}
                onClearAllFilters={clearAllFilters}
            />

            {
                filteredFundData?.length > 0 ? (
                    <Chart id="fund-finalized-chart" dataSource={filteredFundData} height={Math.max(400, new Set(filteredFundData.map((row: any) => row.fund)).size * 32 + 120)} rotated={true} onLegendClick={handleLegendClick} onPointClick={(event) => handleK1PointClick(event, props.siteId, 'Federal and State K-1 Finalized')}>
                        <CommonSeriesSettings argumentField="fund" type="stackedbar" />
                        <Series valueField="finalizedYes" name="Received" color={STATUS_COLOR_MAP.Received || '#888b8d'} visible={(activeSeries.length === 0 || activeSeries.includes('Received')) && showReceived} />
                        <Series valueField="finalizedNo" name="Pending" color={STATUS_COLOR_MAP.Pending || '#888b8d'} visible={(activeSeries.length === 0 || activeSeries.includes('Pending')) && showPending} />
                        <ValueAxis position="bottom" tickInterval={1} allowDecimals={false}><Title text="Count" /></ValueAxis>
                        <ArgumentAxis type="discrete" axisDivisionFactor={1} label={{ overlappingBehavior: 'none' }}><Title text="Fund" /></ArgumentAxis>
                        <Legend verticalAlignment="bottom" horizontalAlignment="center" itemTextPosition="top" />
                        <Export enabled={false} />
                        <Tooltip enabled={true} customizeTooltip={customizeTooltip} />
                    </Chart>
                ) : (
                    <div className="no-data-message">
                        No data available
                    </div>
                )
            }

            <K1DataPointsDialog
                open={isDataViewOpen}
                onClose={() => setIsDataViewOpen(false)}
                title="K1 - Status by Fund - Finalized"
                rows={filteredFundData}
            />
        </div>
    );
}
